import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppliesCategoryProduct } from '../../../../../models/suppliesCategoryProduct';
import { SuppliesDivisionModel } from '../../../../../models/suppliesDivisionModel';
import { generateImageFileNameWithRandomName } from '../../../../../utils/generateImageFileName';
import { MASTER_CATEGORIES_CREATE, MASTER_CATEGORIES_UPDATE } from '../../../../../lib/endpoints/category';
import axios from '../../../../../lib/axios';
import { IMAGE_CATEGORY_ROOT_URL } from '../../../../../constants/imageUrl';


interface Props {
  onOpen: boolean;
  onClose: VoidFunction;
  selectedCat: SuppliesCategoryProduct;
  onfetch: VoidFunction;
  selected: Function;
  DivId: SuppliesDivisionModel;
}

export const useCategoryProductController = ({
  onOpen,
  onClose,
  selectedCat,
  onfetch,
  selected,
  DivId,
}: Props) => {
  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [openConfirmForm, setOpenConfirmForm] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string>(null!);
  const [errorMessage, setErrorMessage] = useState<string>(null!);

  const catCode = useRef<HTMLInputElement>(null!);
  const nameLa = useRef<HTMLInputElement>(null!);
  const nameEn = useRef<HTMLInputElement>(null!);

  const { t } = useTranslation('global');

  //============================> open Event
  const handleOpenForm = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirmForm(true);
    }
  };

  const handleCloseConfirmForm = () => {
    setOpenConfirmForm(false);
  };

  //===========================> function

  const handleReset = () => {
    selected(null);
    setImageToShow(null);
    setImageFile(null);
    catCode.current.value = '';
    nameLa.current.value = '';
    nameEn.current.value = '';
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  const handleValidateForm = () => {
    if (!selectedCat && !imageFile) {
      setImageError('Please selected Image.');
      return false;
    }
    if (imageFile && imageError) {
      setImageError('');
    }
    setErrorMessage('');
    return true;
  };

  const handleSubmit = () => {
    if (selectedCat?.id) {
      handleEditCategoryProduct();
    } else {
      handleAddCategoryProduct();
    }
  };

  const handleAddCategoryProduct = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormInput = {
        code: catCode.current?.value,
        category_name_la: nameLa.current?.value,
        category_name_en: nameEn.current?.value,
        supplies_division_name_id: DivId.id,
        file: generateImageFileNameWithRandomName(imageFile),
      };
      await axios.post(`${MASTER_CATEGORIES_CREATE}`, FormInput, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onClose();
      await onfetch();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
    }
  };

  const handleEditCategoryProduct = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormInput: any = {
        code: catCode.current?.value,
        category_name_la: nameLa.current?.value,
        category_name_en: nameEn.current?.value,
        supplies_division_name_id: DivId.id,
      };
      if (selectedCat) {
        FormInput.file = generateImageFileNameWithRandomName(imageFile);
      }
      await axios.patch(`${MASTER_CATEGORIES_UPDATE + selectedCat.id}`, FormInput, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onClose();
      await onfetch();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
    }
  };

  //==========================> useEffect
  useEffect(() => {
    if (selectedCat) {
      catCode.current.value = selectedCat?.code;
      nameLa.current.value = selectedCat?.category_name_la;
      nameEn.current.value = selectedCat?.category_name_en;
      setImageToShow(IMAGE_CATEGORY_ROOT_URL + selectedCat?.icon?.url);
    }
  }, [selectedCat]);

  return {
    handleSelectImage,
    imageToShow,
    catCode,
    nameLa,
    nameEn,
    imageError,
    errorMessage,
    handleOpenForm,
    openConfirmForm,
    loadingConfirmButton,
    handleCloseConfirmForm,
    handleSubmit,
    handleReset,
  };
};
